import type { StatusBadgeVariant } from '@/shared/components/ui/StatusBadge';
import {
  BookingStatus,
  GuideRequestStatus,
  GuideStatus,
  OnboardingStage,
  ProfileStatus,
  VerificationStatus,
} from '@/types';

export function guideStatusToBadge(status?: string): StatusBadgeVariant {
  switch (status) {
    case GuideStatus.APPROVED:
      return 'approved';
    case GuideStatus.SUBMITTED:
      return 'pending';
    case GuideStatus.UNDER_REVIEW:
      return 'under_review';
    case GuideStatus.REJECTED:
      return 'rejected';
    case GuideStatus.SUSPENDED:
      return 'suspended';
    case GuideStatus.PROFILE_INCOMPLETE:
    case GuideStatus.DRAFT:
      return 'profile_incomplete';
    default:
      // This only understands the legacy GuideStatus values. A lifecycle status
      // used to land here and render a plausible-looking grey chip instead of
      // failing visibly — use the lifecycle mappers below for those.
      if (import.meta.env.DEV && status) {
        console.warn(
          `guideStatusToBadge received an unknown status: "${status}". ` +
            'Lifecycle values need verificationStatusToBadge / ' +
            'profileStatusToBadge / onboardingStageToBadge.'
        );
      }
      return 'profile_incomplete';
  }
}

export function verificationStatusToBadge(status?: string): StatusBadgeVariant {
  switch (status) {
    case VerificationStatus.PASSED:
      return 'approved';
    case VerificationStatus.FAILED:
      return 'rejected';
    case VerificationStatus.IN_PROGRESS:
      return 'under_review';
    case VerificationStatus.PENDING:
      return 'pending';
    default:
      return 'profile_incomplete';
  }
}

export function profileStatusToBadge(status?: string): StatusBadgeVariant {
  switch (status) {
    case ProfileStatus.APPROVED:
      return 'approved';
    case ProfileStatus.SUBMITTED:
      return 'pending';
    case ProfileStatus.UNDER_REVIEW:
      return 'under_review';
    case ProfileStatus.CHANGES_REQUIRED:
      return 'rejected';
    default:
      return 'profile_incomplete';
  }
}

export function onboardingStageToBadge(stage?: string): StatusBadgeVariant {
  switch (stage) {
    case OnboardingStage.LIVE:
      return 'approved';
    case OnboardingStage.REJECTED:
      return 'rejected';
    case OnboardingStage.PROFILE_REVIEW:
      return 'under_review';
    case OnboardingStage.BACKGROUND_CHECK:
    case OnboardingStage.READY_TO_PUBLISH:
      return 'pending';
    case OnboardingStage.SUSPENDED:
      return 'suspended';
    default:
      return 'profile_incomplete';
  }
}

export function guideRequestStatusToBadge(status: string): StatusBadgeVariant {
  switch (status) {
    case GuideRequestStatus.PENDING:
      return 'pending';
    case GuideRequestStatus.APPROVED:
      return 'approved';
    case GuideRequestStatus.REJECTED:
      return 'rejected';
    case GuideRequestStatus.EXPIRED:
    case GuideRequestStatus.WITHDRAWN:
      return 'suspended';
    default:
      return 'profile_incomplete';
  }
}

export function bookingStatusToBadge(status: string): StatusBadgeVariant {
  switch (status) {
    case BookingStatus.PAYMENT_PENDING:
      return 'pending';
    case BookingStatus.CONFIRMED:
    case BookingStatus.UPCOMING:
    case BookingStatus.COMPLETED:
      return 'approved';
    case BookingStatus.IN_PROGRESS:
    case BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION:
      return 'under_review';
    case BookingStatus.DISPUTED:
      return 'rejected';
    case BookingStatus.CANCELLED:
      return 'rejected';
    case BookingStatus.REFUNDED:
    case BookingStatus.EXPIRED:
      return 'suspended';
    default:
      return 'profile_incomplete';
  }
}

export function withdrawalStatusToBadge(status: string): StatusBadgeVariant {
  switch (status) {
    case 'COMPLETED':
      return 'approved';
    case 'REJECTED':
      return 'rejected';
    case 'PENDING':
    case 'APPROVED':
      return 'pending';
    default:
      return 'under_review';
  }
}

export function walletTransactionStatusToBadge(status: string): StatusBadgeVariant {
  switch (status) {
    case 'SUCCESS':
      return 'approved';
    case 'FAILED':
      return 'rejected';
    case 'PENDING':
      return 'pending';
    default:
      return 'under_review';
  }
}

export function formatStatusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}
